import React from 'react'
import { useTheme } from 'styled-components'
import {
  AnalysisMetricsGrid,
  AnalysisMetricCard,
  MetricHeader,
  MetricIcon,
  MetricTitle,
  MetricValue,
  MetricSubtext,
} from '../../../../atoms/AnalysisMetricCard'
import { Icon } from '../../../../atoms/Icon'
import { InfoTooltip } from '../../../../InfoTooltip'
import { TooltipMetricExplanation } from '../../../../TooltipMetricExplanation'
import type { LandMetricsProps } from '../../LandSection.types'

export const LandMetrics: React.FC<LandMetricsProps> = ({
  area,
  buildingCount,
  underExploitedScore,
  className,
  t,
}) => {
  const theme = useTheme()
  const formatArea = (value: number) => `${value.toLocaleString()} m²`
  const formatCount = (value: number) => value.toLocaleString()
  const formatScore = (value: number) => `${Math.round(value)}/100`

  const getScoreColor = (score: number): string => {
    if (score >= 67) return theme.colors.success
    if (score >= 34) return theme.colors.warning
    return theme.colors.danger
  }

  const scoreColor = getScoreColor(underExploitedScore)

  return (
    <AnalysisMetricsGrid className={className}>
      {/* Land Area Metric */}
      <AnalysisMetricCard>
        <MetricHeader>
          <MetricIcon>
            <Icon name="ruler" size="medium" />
          </MetricIcon>
          <MetricTitle>{t('metrics.landArea.title')}</MetricTitle>
        </MetricHeader>
        <MetricValue>{formatArea(area)}</MetricValue>
        <MetricSubtext>{t('metrics.landArea.subtext')}</MetricSubtext>
      </AnalysisMetricCard>

      {/* Buildings Count Metric */}
      <AnalysisMetricCard>
        <MetricHeader>
          <MetricIcon>
            <Icon name="building" size="medium" />
          </MetricIcon>
          <MetricTitle>{t('metrics.buildings.title')}</MetricTitle>
        </MetricHeader>
        <MetricValue>{formatCount(buildingCount)}</MetricValue>
        <MetricSubtext>{t('metrics.buildings.subtext')}</MetricSubtext>
      </AnalysisMetricCard>

      {/* Under Exploited Score Metric */}
      <AnalysisMetricCard>
        <MetricHeader>
          <MetricIcon color={scoreColor}>
            <Icon name="trending-up" size="medium" />
          </MetricIcon>
          <MetricTitle>
            {t('metrics.underExploitedScore.title')}
            <InfoTooltip>
              <TooltipMetricExplanation
                title={t('metrics.underExploitedScore.tooltip.title')}
                shortDescription={t(
                  'metrics.underExploitedScore.tooltip.description'
                )}
                calculation={t(
                  'metrics.underExploitedScore.tooltip.calculation'
                )}
                importance={t('metrics.underExploitedScore.tooltip.importance')}
                source={t('metrics.underExploitedScore.tooltip.source')}
              />
            </InfoTooltip>
          </MetricTitle>
        </MetricHeader>
        <MetricValue style={{ color: scoreColor }}>
          {formatScore(underExploitedScore)}
        </MetricValue>
        <MetricSubtext>
          {t('metrics.underExploitedScore.subtext')}
        </MetricSubtext>
      </AnalysisMetricCard>
    </AnalysisMetricsGrid>
  )
}
